import { useCallback, useMemo, useRef, useState } from 'react'

import { useDropdownAttributeDisplayState } from 'features/views/attributes/hooks/useDropdownAttributeDisplayState'
import { DropdownAttributeBoxedLabelStyles } from 'features/views/LayoutEditor/widgets/HighlightsWidget/attributes/DropdownAttribute.css'
import { HighlightsWidgetAttributeItem } from 'features/views/LayoutEditor/widgets/HighlightsWidget/attributes/types'
import { useRecordManagerFieldValue } from 'features/views/RecordManager/hooks/useRecordManagerFieldValue'

type UseDropdownAttributeBoxedStateProps = {
    value?: string | string[]
    item: HighlightsWidgetAttributeItem
    isLoading?: boolean
}

export function useDropdownAttributeBoxedState({
    item,
    isLoading,
}: UseDropdownAttributeBoxedStateProps) {
    const value = useRecordManagerFieldValue<string | string[]>(item.field.api_name)

    const {
        options,
        isOverflowPopupOpen,
        onOverflowLabelFocus,
        onOverflowLabelBlur,
        onOverflowLabelMouseEnter,
        onOverflowLabelMouseLeave,
        onOverflowPopupOpenChange,
    } = useDropdownAttributeDisplayState({
        field: item.field,
        value,
        isLoading,
    })
    const onOptionClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation()
    }, [])

    const attributeRef = useRef<HTMLDivElement>(null)

    const initialOptionsLength =
        attributeRef.current?.querySelectorAll(
            `.${DropdownAttributeBoxedLabelStyles.styleFunction({})}`
        )?.length ?? 0
    const [visibleOptionsLength, setVisibleOptionsLength] = useState(initialOptionsLength)

    const onTruncate = useCallback(() => {
        const optionsLength =
            attributeRef.current?.querySelectorAll(
                `.${DropdownAttributeBoxedLabelStyles.styleFunction({})}`
            )?.length ?? 0
        setVisibleOptionsLength(optionsLength)
    }, [])

    return useMemo(
        () => ({
            options,
            onOptionClick,
            attributeRef,
            isOverflowPopupOpen,
            onOverflowLabelFocus,
            onOverflowLabelBlur,
            onOverflowLabelMouseEnter,
            onOverflowLabelMouseLeave,
            onOverflowPopupOpenChange,
            visibleOptionsLength,
            onTruncate,
        }),
        [
            options,
            onOptionClick,
            isOverflowPopupOpen,
            onOverflowLabelFocus,
            onOverflowLabelBlur,
            onOverflowLabelMouseEnter,
            onOverflowLabelMouseLeave,
            onOverflowPopupOpenChange,
            visibleOptionsLength,
            onTruncate,
        ]
    )
}
